/**
 * Cross-cutting signal for "this account no longer has access".
 *
 * Lives in its own module because both sides need it: `api.ts` raises it when a
 * response comes back 403 `ACCOUNT_INACTIVE`, and `auth.tsx` handles it by
 * wiping the session. Importing one from the other directly would close an
 * import cycle (api.ts already pulls the token accessors out of auth.tsx).
 */
export type AccountInactiveReason = "terminated" | "deactivated";

let _handler: ((reason: AccountInactiveReason) => void) | null = null;

export function setAccountInactiveHandler(
    fn: ((reason: AccountInactiveReason) => void) | null
): void {
    _handler = fn;
}

/**
 * Inspects a failed response for the account-inactive signal.
 *
 * Called for every non-OK response so the lockout lands no matter which screen
 * happened to make the request.
 */
export function notifyIfAccountInactive(status: number, envelope: unknown): void {
    if (status !== 403 || !envelope || typeof envelope !== "object") {
        return;
    }
    const e = envelope as { code?: unknown; reason?: unknown };
    if (e.code !== "ACCOUNT_INACTIVE") {
        return;
    }
    _handler?.(e.reason === "deactivated" ? "deactivated" : "terminated");
}
